import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { SectionView } from "@/components/pageSections";
import { TIDS } from "@/constants/testIds";
import useSeo from "@/hooks/useSeo";
import {
  Sparkles, Zap, MessageCircle, Video, Wallet, LineChart, ShieldCheck,
  ArrowRight, Check, ChevronDown, Star, Users, Globe2, Palette, PlayCircle,
  Trophy, TrendingUp, Clock3, Award, Shield, Layers, Gift, Rocket,
} from "lucide-react";

// ------------- Stock imagery -------------
const IMG = {
  host1: "https://images.unsplash.com/photo-1758336011136-343678e4fc05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjB0ZWFjaGVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg0NTQ5MjE0fDA&ixlib=rb-4.1.0&q=85",
  host2: "https://images.unsplash.com/photo-1622460241950-a4cad772e07e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjB0ZWFjaGVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg0NTQ5MjE0fDA&ixlib=rb-4.1.0&q=85",
  workspace: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
  webcam: "https://images.unsplash.com/photo-1587440871875-191322ee64b0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
  laptop: "https://images.unsplash.com/photo-1531482615713-2afd69097998?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
  chart: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
  cohort: "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
  city: "https://images.unsplash.com/photo-1596178065887-1198b6148b2b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
};

// ------------- Problem → Solution matcher -------------
const PROBLEMS = [
  { id: "no-shows", label: "Attendees register but don't show up", icon: Users, feature: "automation",
    headline: "Bring your attendance rate from 30% → 70%+",
    body: "Auto WhatsApp + email reminders at 24hr, 1hr and 10min before the session. Meta-approved BSP, delivered in seconds. India's #1 no-show killer.",
    proof: "Riya's cohort jumped from 38% → 71% attendance in 3 weeks." },
  { id: "manual-work", label: "Manual reminders eat my day", icon: Clock3, feature: "automation",
    headline: "12+ hours a week — back in your calendar",
    body: "Visual automation builder. Once configured, every registrant gets Zoom link, reminders, follow-ups, recording delivery, and post-webinar surveys on autopilot.",
    proof: "Arjun cut his ops time from 14 hrs/week → 90 minutes." },
  { id: "landing-page", label: "I hate designing landing pages", icon: Palette, feature: "landing-ai",
    headline: "AI writes your landing page in 40 seconds",
    body: "Paste your topic. Get headline, sub-copy, agenda, host bio, testimonial suggestions — all in your voice. Publish on your own custom domain. No designer, no delay.",
    proof: "200 seats sold at ₹499 the same evening — from a page written in one coffee break." },
  { id: "zoom-setup", label: "Zoom setup is a nightmare", icon: Video, feature: "zoom",
    headline: "Zero-setup Zoom meetings",
    body: "Videoepreneur's shared license pool handles Zoom for you. Each attendee gets a unique tracked join link. Zero admin, zero double-bookings.",
    proof: "Coaches save ₹1,499/mo on Zoom Pro licenses per host." },
  { id: "payment", label: "Payments and coupons are messy", icon: Wallet, feature: "payments",
    headline: "UPI + coupons + invoices — one flow",
    body: "Razorpay-native checkout with UPI, cards, netbanking. Discount codes, waitlist upgrades, GST invoices — all built in. Auto-refund on cancellations.",
    proof: "Business coach Priya recovered 22% of failed payments via nudges." },
  { id: "analytics", label: "I can't tell what's actually working", icon: LineChart, feature: "analytics",
    headline: "Revenue-first analytics dashboards",
    body: "Funnel view: visitors → registrations → attended → paid. UTM source breakdown, drop-off points, hour-by-hour revenue. Speaker deck PDF exports.",
    proof: "Sagar shifted 40% of ad spend to Instagram after seeing UTM data." },
];

// ------------- Feature deep-dives -------------
const FEATURES = [
  {
    icon: Sparkles, badge: "AI Content Studio",
    title: "Your writing team. On tap. In Hinglish or English.",
    body: "Titles that convert. Landing copy that reads like you wrote it. Email + WhatsApp templates ready to ship. Audio-friendly speaker script. All in ≤ 40 seconds.",
    bullets: ["Powered by Claude Sonnet 4.5", "Save entire drafts to Templates", "Regenerate any block in seconds"],
    image: IMG.laptop,
  },
  {
    icon: Zap, badge: "Automation Engine",
    title: "Set-and-forget workflows that actually convert.",
    body: "Visual builder for reminder cadences, drip series, no-show follow-ups, testimonial requests, review nudges. Fires the moment a trigger event happens — no cron, no waiting.",
    bullets: ["Delay + Wait steps in days/hours/minutes", "Branch on payment_status / attended", "Email + WhatsApp channels"],
    image: IMG.workspace,
  },
  {
    icon: Globe2, badge: "White-label Custom Domains",
    title: "Serve everything on your own domain.",
    body: "Point webinar.yoursite.com at Videoepreneur. Your landing pages, portals, certificates — all yours. Zero platform branding visible to your audience.",
    bullets: ["CNAME + TXT auto-verification", "Auto-SSL provisioning", "Pro (1 domain) · Business (5 domains)"],
    image: IMG.city,
  },
  {
    icon: LineChart, badge: "Analytics",
    title: "Every rupee, tracked.",
    body: "Funnel by webinar, revenue by hour, UTM source breakdown, show-up rate, no-show list export. Download your speaker deck as PDF for offline sharing.",
    bullets: ["Recharts-powered visualisations", "CSV export for finance", "Registrant search + filter"],
    image: IMG.chart,
  },
  {
    icon: Layers, badge: "Cohorts & Courses",
    title: "Bundle webinars into paid cohorts — in minutes.",
    body: "Drip mode unlocks modules weekly. Cohort mode gets everyone live together. One enrollment auto-registers your attendee into every module — zero manual work.",
    bullets: ["Cohort or drip schedules", "One paywall, many modules", "Attendee portal per learner"],
    image: IMG.cohort,
  },
  {
    icon: MessageCircle, badge: "Live Engagement + AI Co-pilot",
    title: "Never freeze on a question again.",
    body: "Real-time Q&A stream + reaction wall. AI co-pilot drafts a 60-second answer for every question — read aloud, edit inline, mark answered. Your live sessions will feel unfair.",
    bullets: ["Live reactions counter", "AI-suggested answers per Q", "Attendee portal auto-updates"],
    image: IMG.webcam,
  },
];

// ------------- Case studies -------------
const CASES = [
  {
    name: "Riya Malhotra", role: "Business coach · Mumbai", img: IMG.host2,
    metric_label: "Attendance jump", metric_value: "+87%",
    story: "Riya used to spend Sunday nights copy-pasting WhatsApp reminders. She switched her ₹4,999 cohort to Videoepreneur's automation engine. Show-up rate went from 38% to 71%. Revenue per cohort tripled.",
    tags: ["Automation", "Cohorts", "WhatsApp reminders"],
  },
  {
    name: "Arjun Sethi", role: "YouTuber (120k) · Delhi", img: IMG.host1,
    metric_label: "Seats sold in 6 hours", metric_value: "212",
    story: "Arjun's audience is loyal but skeptical. He plugged in his topic, let AI Content Studio draft the landing page, and shipped in 90 minutes. 212 seats at ₹499 sold the same evening — before ads even kicked in.",
    tags: ["AI Studio", "Custom domain", "Razorpay"],
  },
  {
    name: "Coach Aditi", role: "Career coach · Bengaluru", img: IMG.host2,
    metric_label: "Ops time reduced", metric_value: "−12 hrs/week",
    story: "Aditi runs a monthly cohort. She replaced her Slack + Google Sheets + Zoom + Mailchimp stack with Videoepreneur. She now spends 90 minutes/week on ops instead of 14 — and her NPS went up to 74.",
    tags: ["Cohorts", "Analytics", "Templates"],
  },
];

// ------------- Testimonials wall -------------
const TESTI = [
  { q: "The AI script in 40 seconds sold me. I published my first paid webinar in a lunch break.", n: "Nikhil V.", r: "Product coach" },
  { q: "WhatsApp reminders 10min before the live are magic. Attendance is finally predictable.", n: "Sneha R.", r: "Yoga educator" },
  { q: "The certificate + recording delivery feels like a proper school. My students love it.", n: "Prof. Iqbal", r: "Ed-creator" },
  { q: "Custom domain + white-label footer means my brand stays front and center. Priceless.", n: "Kavya S.", r: "Design mentor" },
  { q: "Two cohorts in and I'm generating more revenue than my last 6 months on YouTube.", n: "Rahul M.", r: "Fitness creator" },
  { q: "AI Q&A co-pilot literally made me sound smarter live. Not even kidding.", n: "Ananya T.", r: "Career coach" },
];

// ------------- Pricing tiers -------------
const PRICING = [
  { key: "free", label: "Free", price: 0, perks: ["Unlimited webinars on videoepreneur.com", "AI Content Studio (10 gens/mo)", "Basic reminders", "Community support"], cta: "Start free" },
  { key: "pro", label: "Pro", price: 1499, popular: true, perks: ["Everything in Free", "1 custom domain + auto-SSL", "Unlimited AI generations", "Priority WhatsApp reminders", "Advanced analytics + PDF exports", "Email support"], cta: "Try Pro free" },
  { key: "business", label: "Business", price: 4999, perks: ["Everything in Pro", "Up to 5 custom domains", "White-label branding", "Team seats + workspace roles", "Cohort/course module unlocked", "Dedicated CSM"], cta: "Talk to sales" },
];

// ------------- FAQ -------------
const FAQ = [
  { q: "Zoom Pro/Webinar plan chahiye kya extra?", a: "Nahi. Videoepreneur ka shared Zoom license pool har webinar ke liye alag meeting banata hai. Har attendee ko unique tracked join link milta hai. Tumhare ₹1,499–₹15,000/mo Zoom pe save hote hain." },
  { q: "Videoepreneur vs Zoom Webinar — real difference kya hai?", a: "Zoom Webinar sirf hosting deta hai (₹15K/mo). Videoepreneur me sab kuch — landing page, Razorpay checkout, WhatsApp/email reminders, certificate PDF, custom domain, AI content — ₹1,199/mo se. Sath me Hinglish tone, Indian creators ke liye tuned." },
  { q: "WhatsApp reminders spam nahi ho jaayenge?", a: "Nahi — hum Meta ke official WhatsApp Cloud API (BSP-approved) use karte hain. Sab reminders pre-approved template messages hain, seconds me deliver hote hain. 92% delivery rate — spam risk zero." },
  { q: "Kya main apni domain (mystartup.in) use kar sakta hu?", a: "Haan — Pro aur Business plan me. Ek CNAME + TXT record daalo, Verify click karo, tumhari landing page + attendee portal `webinar.yoursite.com` pe serve hone lagenge. SSL auto-provision. Videoepreneur branding kahin nahi dikhega attendees ko." },
  { q: "AI Content Studio Hinglish me kaam karta hai?", a: "Bilkul. Titles, landing copy, agenda, host bio, email drip, WhatsApp templates, aur pura speaker script — sab Hinglish ya English me. Kimi + Claude Sonnet 4.5 backed. Har block regenerate + edit ho sakta hai." },
  { q: "Payment India-friendly hai kya?", a: "Razorpay native integration — UPI, cards, netbanking, wallets, EMI. GST invoices auto-generate, coupon codes kaam karte hain, refunds one-click. INR-native throughout — no forex nonsense." },
  { q: "Webinar khatm hone ke baad kya hota hai?", a: "Recording upload hote hi hum auto-deliver karte hain email + WhatsApp se. Attend nahi kiye they? Unhe 'You missed it' replay email jaata hai. Certificate PDF har paid attendee ke liye auto-generate — personalised name + date ke saath." },
  { q: "Team ke saath collaborate kar sakte hain?", a: "Business plan me multi-seat workspace — Owner, Admin, Editor, Viewer roles. Ek team, ek workspace, sab webinars/templates/analytics shared. Coaching institutes ke liye perfect." },
  { q: "Free trial hai? Card details chahiye?", a: "Card nahi chahiye. Free plan me unlimited webinars videoepreneur.com pe host karo, 10 AI generations/month, basic reminders. Pehle apni pehli sale karo, phir upgrade karo." },
  { q: "Refund policy?", a: "14 din ka money-back guarantee — Pro aur Business dono me. Koi sawal nahi. Bas support ko email karo aur pura refund yearly bill par bhi milta hai." },
  { q: "Kya main apne existing landing page/CRM se integrate kar sakta hu?", a: "Haan — Zapier, Webhooks, CSV export/import sab available. UTM tracking built-in hai, aur analytics me source-wise breakdown milega." },
  { q: "Data India me stored hota hai kya?", a: "Haan — hamari infrastructure India (Mumbai) me primary hosted hai. GDPR + India ke DPDP Act dono compliant. Attendee PII encrypted at rest." },
];

// ============= Hero webinar mockup (MacBook + live session UI) =============
function HeroWebinarMockup() {
  const users = [
    { i: "SG", n: "Sanskriti Gupta", c: "#22C55E" },
    { i: "AP", n: "Aarav Patel",    c: "#0B5CFF" },
    { i: "DS", n: "Diya Sharma",    c: "#F5A623" },
    { i: "AV", n: "Aryan Verma",    c: "#EC4899" },
    { i: "PK", n: "Priya Kapoor",   c: "#EA580C" },
    { i: "RS", n: "Rohan Singh",    c: "#DC2626" },
  ];

  return (
    <div className="relative w-full mx-auto">
      {/* MacBook shell */}
      <div className="relative rounded-[22px] p-2 bg-gradient-to-b from-[#2A2D3A] to-[#0E0F16] shadow-[0_40px_80px_-20px_rgba(11,13,23,0.55),0_10px_28px_-10px_rgba(11,13,23,0.4)]">
        {/* Top bezel with camera dot */}
        <div className="h-4 flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-full bg-[#3D414F]" />
        </div>
        {/* Screen */}
        <div className="rounded-[14px] overflow-hidden bg-[#0B0D17] aspect-[16/10] relative">
          {/* Blue meeting canvas */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1FDB] via-[#0B1AC8] to-[#080F86]">
            {/* Central presenter */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="w-[22%] max-w-[220px] aspect-square rounded-full overflow-hidden ring-4 ring-white/25 shadow-[0_18px_40px_rgba(0,0,0,0.4)]"
                     style={{ minWidth: 100 }}>
                  {/* SVG presenter avatar — safe illustration (no stock photo) */}
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <radialGradient id="bg-pres" cx="50%" cy="35%">
                        <stop offset="0%" stopColor="#3B2416" />
                        <stop offset="100%" stopColor="#171016" />
                      </radialGradient>
                    </defs>
                    <rect width="100" height="100" fill="url(#bg-pres)" />
                    {/* hair */}
                    <path d="M22 44 C22 26, 40 18, 50 18 C60 18, 78 26, 78 46 L78 60 C78 60, 76 46, 68 42 C64 44, 58 45, 50 45 C42 45, 36 44, 32 42 C24 46, 22 60, 22 60 Z" fill="#2A1810" />
                    {/* face */}
                    <ellipse cx="50" cy="52" rx="22" ry="24" fill="#E4B996" />
                    {/* smile */}
                    <path d="M40 60 Q50 70 60 60" stroke="#3B2416" strokeWidth="2" fill="none" strokeLinecap="round" />
                    {/* eyes */}
                    <ellipse cx="42" cy="50" rx="1.8" ry="2.4" fill="#1A1A1A" />
                    <ellipse cx="58" cy="50" rx="1.8" ry="2.4" fill="#1A1A1A" />
                    {/* headset band */}
                    <path d="M28 42 Q50 20 72 42" stroke="#1A1A1A" strokeWidth="3.5" fill="none" strokeLinecap="round" />
                    {/* headset ear */}
                    <ellipse cx="27" cy="52" rx="4" ry="6" fill="#1A1A1A" />
                    {/* mic */}
                    <path d="M27 58 Q34 66 44 66" stroke="#1A1A1A" strokeWidth="2" fill="none" />
                    <circle cx="44" cy="66" r="2" fill="#DC2626" />
                    {/* shoulders */}
                    <path d="M20 100 C20 82, 30 76, 50 76 C70 76, 80 82, 80 100 Z" fill="#8B4A6A" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Info card bottom-left */}
            <div className="absolute bottom-16 left-4 sm:bottom-20 sm:left-6 max-w-[32%]">
              <div className="rounded-lg bg-[#1D1F27]/95 backdrop-blur px-3.5 py-3 text-white shadow-lg">
                <div className="text-sm font-semibold">Webinar Day 1</div>
                <div className="mt-2 flex items-center gap-1.5 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                  <span className="text-[#22C55E] font-semibold">Live</span>
                </div>
                <div className="mt-2.5 space-y-1 text-[11px] text-white/70">
                  <div>📅 Today at 1:00 PM</div>
                  <div>👥 187 participants</div>
                  <div className="hidden sm:block">🌐 Open webinar</div>
                </div>
                <div className="mt-2.5 pt-2.5 border-t border-white/10 hidden sm:block">
                  <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1.5">Share webinar</div>
                  <div className="flex gap-1.5">
                    <div className="text-[10px] px-2 py-1 rounded bg-white/10 text-white">🔗 Copy link</div>
                    <div className="text-[10px] px-2 py-1 rounded bg-white/10 text-white">↗ Invite</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right sidebar — chat/users panel */}
            <div className="absolute top-0 right-0 h-full w-[36%] max-w-[240px] bg-[#0B0D17]/95 backdrop-blur-sm border-l border-white/10 flex flex-col">
              {/* Sidebar header */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/10">
                <div className="text-white text-sm font-semibold">Chat</div>
                <div className="flex gap-2 text-white/50">
                  <span className="text-xs">⟲</span>
                  <span className="text-xs">⊞</span>
                  <span className="text-xs">✕</span>
                </div>
              </div>
              {/* Tabs */}
              <div className="grid grid-cols-2 border-b border-white/10">
                <div className="text-center py-2 text-xs text-white/50">Messages</div>
                <div className="text-center py-2 text-xs text-white font-semibold border-b-2 border-[#0B5CFF]">Users (237)</div>
              </div>
              {/* Online */}
              <div className="px-3 py-2 flex items-center justify-between border-b border-white/5">
                <div className="text-[11px] text-white/50 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" /> Online (237)</div>
                <span className="text-[10px] text-white/30">⇩</span>
              </div>
              {/* Users list */}
              <div className="flex-1 overflow-hidden">
                {users.map((u, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-3 py-2 border-b border-white/5">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0" style={{ background: u.c }}>
                      {u.i}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white font-medium truncate">{u.n}</div>
                      <div className="text-[10px] text-[#22C55E]">Active now</div>
                    </div>
                    <span className="text-[9px] text-white/30">⟲</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom toolbar */}
            <div className="absolute bottom-0 left-0 right-[36%] px-4 py-2.5 flex items-center gap-2 sm:gap-2.5 bg-gradient-to-t from-[#0B0D17]/80 to-transparent">
              <div className="text-xs text-white/70 mr-1">Webinar Day 1</div>
              <div className="text-xs text-white/50">👥 1</div>
              <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center text-white/80 text-xs">📊</div>
              <div className="flex-1" />
              <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center text-white text-sm">🎤</div>
              <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center text-white text-sm">📹</div>
              <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center text-white text-sm hidden sm:flex">🖥</div>
              <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center text-white text-sm hidden sm:flex">⭕</div>
              <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center text-white text-sm hidden sm:flex">⊞</div>
              <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center text-white text-sm">💬</div>
              <div className="px-3 py-1.5 rounded-md bg-[#DC2626] text-white text-xs font-bold">End</div>
            </div>
          </div>
        </div>
        {/* Bottom bezel */}
        <div className="h-2" />
      </div>
      {/* MacBook base */}
      <div className="relative mx-auto h-2 max-w-[110%] rounded-b-2xl bg-gradient-to-b from-[#1A1D28] to-[#0A0B12] shadow-[0_20px_20px_-14px_rgba(0,0,0,0.4)]" style={{ width: "108%", marginLeft: "-4%" }} />
      <div className="relative mx-auto h-1 rounded-b-full bg-[#0A0B12]" style={{ width: "42%" }} />

      {/* --- Floating overlays --- */}
      {/* Chat bubbles (left side) — sit OUTSIDE the laptop bezel */}
      <div className="absolute top-[4%] left-2 sm:-left-2 md:-left-6 flex flex-col items-start gap-2.5 max-w-[54%] sm:max-w-[36%] pointer-events-none z-10">
        {[
          { t: "That's amazing! 😍", d: 0 },
          { t: "Excited for the next session!", d: 120 },
          { t: "I've a doubt in point 4 ✋", d: 240 },
        ].map((b, i) => (
          <div
            key={i}
            className="bg-white rounded-2xl rounded-bl-md px-3 py-2 shadow-[0_10px_30px_-8px_rgba(11,13,23,0.35)] text-xs sm:text-sm font-medium text-[#0B0D17] border border-black/5 vp-fade-up whitespace-nowrap"
            style={{ animationDelay: `${b.d}ms` }}
          >
            {b.t}
          </div>
        ))}
      </div>

      {/* Join Webinar notification (top-right, floats outside) */}
      <div className="absolute -top-3 sm:-top-4 right-2 sm:-right-2 md:-right-6 bg-white rounded-2xl px-3 py-2.5 shadow-[0_14px_36px_-10px_rgba(11,13,23,0.25)] border border-black/5 flex items-center gap-2.5 pointer-events-none z-10">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0ABFC] to-[#FCE7F3] flex items-center justify-center text-[#DB2777] text-lg font-bold">+</div>
        <div>
          <div className="text-xs sm:text-sm font-display font-bold text-[#0B0D17] whitespace-nowrap">Join Webinar</div>
          <div className="text-[10px] text-[#747680] whitespace-nowrap">Connect to a live session</div>
        </div>
      </div>

      {/* Floating reaction emojis (bottom-right, drops below the base) */}
      <div className="absolute -bottom-4 right-4 sm:right-8 flex items-end gap-1 pointer-events-none">
        <div className="text-2xl sm:text-3xl -mb-1 rotate-[-8deg] drop-shadow-lg">👋</div>
        <div className="text-2xl sm:text-3xl -mb-3 drop-shadow-lg">🔥</div>
        <div className="text-2xl sm:text-3xl -mb-2 drop-shadow-lg">❤️</div>
        <div className="text-2xl sm:text-3xl -mb-4 rotate-[8deg] drop-shadow-lg">👍</div>
        <div className="text-2xl sm:text-3xl -mb-1 drop-shadow-lg">😂</div>
      </div>

      {/* Revenue floating chip — bottom-left, outside base */}
      <div className="absolute -bottom-3 left-4 sm:left-2 lg:-left-6 bg-white rounded-xl px-3.5 py-2 shadow-[0_10px_30px_-8px_rgba(11,13,23,0.35)] border border-black/5 pointer-events-none hidden sm:block z-10">
        <div className="text-[9px] text-[#747680] uppercase tracking-widest font-semibold whitespace-nowrap">Aaj ki revenue</div>
        <div className="font-display font-black text-lg text-[#0B5CFF] mt-0.5 whitespace-nowrap">₹1.4L</div>
      </div>
    </div>
  );
}

// ============= Live activity ticker =============
function LiveTicker() {
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/live-ticker?limit=8`);
        const d = await r.json();
        if (alive) setEvents(d.events || []);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 60_000);      // refresh feed every 60s
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (events.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % events.length), 3500);
    return () => clearInterval(t);
  }, [events.length]);

  if (!events.length) return null;
  const e = events[idx % events.length];

  return (
    <div
      key={idx}
      className="mt-6 inline-flex items-center gap-3 px-3.5 py-2.5 rounded-full bg-white/90 backdrop-blur border border-[#E4E6EC] shadow-[0_4px_18px_rgba(0,0,0,0.04)] vp-fade-up max-w-full"
      data-testid="marketing-live-ticker"
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#1E8E3E] opacity-60 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1E8E3E]" />
      </span>
      <div className="text-xs sm:text-sm text-[#0B0D17] truncate min-w-0">
        <span className="font-bold">{e.name}</span>
        <span className="text-[#4A4D57]"> ne </span>
        <span className="font-mono font-bold text-[#0B5CFF]">₹{(e.amount || 0).toLocaleString("en-IN")}</span>
        <span className="text-[#4A4D57]"> me </span>
        <span className="hidden sm:inline text-[#4A4D57]">"{e.topic.length > 32 ? e.topic.slice(0, 32) + "…" : e.topic}"</span>
        <span className="sm:hidden text-[#4A4D57]">"{e.topic.length > 18 ? e.topic.slice(0, 18) + "…" : e.topic}"</span>
        <span className="text-[#4A4D57]"> book kiya</span>
      </div>
      <span className="text-[10px] font-mono text-[#747680] shrink-0 uppercase tracking-widest">{e.ago}</span>
    </div>
  );
}

// ============= On-brand feature mockups =============
function FeatureMockup({ badge }) {
  const shells = {
    "AI Content Studio": (
      <div className="p-4 space-y-3 h-full flex flex-col justify-center">
        <div className="text-[10px] uppercase tracking-widest text-[#747680] font-bold">AI CONTENT STUDIO</div>
        <div className="flex gap-2 flex-wrap">
          {["Landing copy", "Email drip", "WhatsApp reminders", "Speaker script"].map((k) => (
            <div key={k} className="px-2.5 py-1 rounded-full bg-[#0B5CFF]/10 text-[#0B5CFF] text-[10px] font-semibold border border-[#0B5CFF]/25">{k}</div>
          ))}
        </div>
        <div className="rounded-lg bg-white/70 p-3 border border-[#E4E6EC] backdrop-blur-sm">
          <div className="text-[9px] text-[#747680] uppercase tracking-widest">Topic input</div>
          <div className="text-sm text-[#0B0D17] font-medium mt-1">"Apna pehla cohort launch karo — 7-day roadmap"</div>
        </div>
        <div className="text-center text-[10px] text-[#747680] font-mono">⚡ generating in Hinglish · 38s left</div>
        <div className="rounded-lg bg-gradient-to-br from-[#0B5CFF]/8 to-[#7C3AED]/8 p-3 border border-[#7C3AED]/25">
          <div className="text-[9px] text-[#7C3AED] uppercase tracking-widest font-bold">Landing headline</div>
          <div className="text-base font-display font-black text-[#0B0D17] mt-1 leading-snug">Apna pehla ₹50K cohort — bina team ke, 7 dinon me.</div>
        </div>
      </div>
    ),
    "Automation Engine": (
      <div className="p-4 h-full flex flex-col justify-center">
        <div className="text-[10px] uppercase tracking-widest text-[#747680] font-bold mb-4">AUTOMATION TIMELINE</div>
        <div className="relative pl-6 space-y-3 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:from-[#0B5CFF] before:to-[#EA580C]">
          {[
            { t: "Registration", ch: "•", c: "#0B5CFF", desc: "Webinar link + Zoom join sent instantly" },
            { t: "24 hrs before", ch: "WA", c: "#25D366", desc: "WhatsApp reminder — auto template" },
            { t: "1 hour before", ch: "@", c: "#EA580C", desc: "Email + WhatsApp double-tap" },
            { t: "10 min before", ch: "⏰", c: "#DC2626", desc: "'Session shuru hone wala hai' ping" },
            { t: "After webinar", ch: "🏆", c: "#059669", desc: "Recording + certificate + review request" },
          ].map((n, i) => (
            <div key={i} className="relative">
              <div className="absolute -left-6 top-0 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white" style={{ background: n.c }}>{n.ch}</div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-[#0B0D17]">{n.t}</div>
                <div className="text-[9px] text-[#747680] font-mono">sent</div>
              </div>
              <div className="text-[10px] text-[#4A4D57]">{n.desc}</div>
            </div>
          ))}
        </div>
      </div>
    ),
    "White-label Custom Domains": (
      <div className="p-4 h-full flex flex-col justify-center gap-3">
        <div className="text-[10px] uppercase tracking-widest text-[#747680] font-bold">DOMAINS</div>
        <div className="rounded-lg bg-white border border-[#E4E6EC] p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#0B5CFF]/10 flex items-center justify-center"><Globe2 className="w-4 h-4 text-[#0B5CFF]" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-mono font-semibold truncate">webinar.mystartup.in</div>
            <div className="text-[10px] text-[#1E8E3E] font-semibold inline-flex items-center gap-1 mt-0.5">
              <Check className="w-3 h-3" strokeWidth={3} /> Verified · SSL active
            </div>
          </div>
          <div className="text-[10px] text-[#747680] font-mono">primary</div>
        </div>
        <div className="rounded-lg bg-[#F6F7FA] border border-[#E4E6EC] p-3 space-y-1.5">
          <div className="flex items-center justify-between text-[10px] font-mono">
            <span className="text-[#747680]">CNAME</span>
            <span className="text-[#0B0D17] font-bold">cname.videoepreneur.com</span>
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono">
            <span className="text-[#747680]">TXT</span>
            <span className="text-[#0B0D17] font-bold">vp-verify=a7d3…</span>
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono">
            <span className="text-[#747680]">SSL</span>
            <span className="text-[#1E8E3E] font-bold">Auto-provisioned</span>
          </div>
        </div>
        <div className="text-[10px] text-[#747680] text-center">Attendees dekhte hain sirf tumhari brand. Zero Videoepreneur footer.</div>
      </div>
    ),
    "Analytics": (
      <div className="p-4 h-full flex flex-col justify-center gap-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[#747680] font-bold">REVENUE FUNNEL</div>
          <div className="text-[10px] font-mono text-[#0B5CFF]">Last 30 days</div>
        </div>
        {[
          { label: "Visits", val: 4820, pct: 100 },
          { label: "Registrations", val: 1240, pct: 26 },
          { label: "Paid", val: 486, pct: 39 },
          { label: "Attended", val: 371, pct: 76 },
        ].map((r) => (
          <div key={r.label}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-[#0B0D17] font-medium">{r.label}</span>
              <span className="text-[#4A4D57] font-mono text-[10px]">{r.val.toLocaleString("en-IN")} <span className="text-[#0B5CFF] font-bold">{r.pct}%</span></span>
            </div>
            <div className="h-2 rounded-full bg-[#F1F3F7] overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-[#0B5CFF] to-[#7C3AED]" style={{ width: `${r.pct}%` }} />
            </div>
          </div>
        ))}
        <div className="mt-2 grid grid-cols-3 gap-2">
          {[
            { l: "Revenue", v: "₹4.8L" }, { l: "AOV", v: "₹995" }, { l: "Show-up", v: "76%" },
          ].map((s) => (
            <div key={s.l} className="rounded-lg bg-white border border-[#E4E6EC] p-2 text-center">
              <div className="font-display font-black text-lg text-[#0B0D17]">{s.v}</div>
              <div className="text-[9px] uppercase tracking-widest text-[#747680] font-semibold">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    ),
    "Cohorts & Courses": (
      <div className="p-4 h-full flex flex-col justify-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[#747680] font-bold">CREATOR GROWTH BOOTCAMP · COHORT #4</div>
        <div className="grid gap-2">
          {[
            { n: "01", t: "Audience blueprint", s: "Attended", c: "#1E8E3E" },
            { n: "02", t: "Landing page in a day", s: "Attended", c: "#1E8E3E" },
            { n: "03", t: "Payment + funnel", s: "Live now", c: "#DC2626" },
            { n: "04", t: "WhatsApp automation", s: "Unlocks Fri", c: "#747680" },
            { n: "05", t: "Launch playbook", s: "Locked", c: "#747680" },
          ].map((m) => (
            <div key={m.n} className="rounded-lg bg-white border border-[#E4E6EC] p-2.5 flex items-center gap-3">
              <div className="w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-mono font-bold" style={{ background: m.c + "15", color: m.c }}>{m.n}</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-[#0B0D17] truncate">{m.t}</div>
              </div>
              <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: m.c }}>{m.s}</div>
            </div>
          ))}
        </div>
        <div className="mt-1 flex items-center gap-2 text-[10px]">
          <div className="flex-1 h-1.5 rounded-full bg-[#F1F3F7]"><div className="h-full rounded-full bg-gradient-to-r from-[#0B5CFF] to-[#7C3AED]" style={{ width: "45%" }} /></div>
          <div className="font-mono font-bold text-[#0B5CFF]">45%</div>
        </div>
      </div>
    ),
    "Live Engagement + AI Co-pilot": (
      <div className="p-4 h-full flex flex-col justify-center gap-2">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] animate-pulse" /> LIVE · 187 attendees
          </div>
          <div className="text-[10px] font-mono text-[#747680]">42:18</div>
        </div>
        <div className="rounded-lg bg-white border border-[#E4E6EC] p-2.5 space-y-2">
          <div className="text-[10px] text-[#747680] uppercase tracking-widest font-semibold">Live question · Priya S.</div>
          <div className="text-sm text-[#0B0D17]">"Zero audience se paid cohort kaise launch karun?"</div>
        </div>
        <div className="rounded-lg bg-gradient-to-br from-[#7C3AED]/8 to-[#0B5CFF]/8 border border-[#7C3AED]/30 p-2.5 space-y-2">
          <div className="text-[10px] text-[#7C3AED] uppercase tracking-widest font-bold inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI Co-pilot suggestion</div>
          <div className="text-xs text-[#0B0D17] leading-snug">"Priya, pehle apni story ka ek clean landing page banao — free launch webinar ka. 100 registrations aayenge apne network se. Fir usme se 30 warm leads DM karo…"</div>
          <div className="flex gap-2 pt-1">
            <button className="text-[10px] px-2 py-1 rounded bg-[#7C3AED] text-white font-bold">Read aloud ▸</button>
            <button className="text-[10px] px-2 py-1 rounded bg-white border border-[#E4E6EC] font-semibold">Edit</button>
            <button className="text-[10px] px-2 py-1 rounded bg-[#1E8E3E]/10 text-[#1E8E3E] font-bold">✓ Answered</button>
          </div>
        </div>
        <div className="flex gap-1.5 justify-center pt-1">
          {["❤️ 42", "🙌 28", "🔥 19", "💡 12"].map((r) => (
            <div key={r} className="text-[10px] px-2 py-1 rounded-full bg-[#F1F3F7] font-semibold text-[#0B0D17]">{r}</div>
          ))}
        </div>
      </div>
    ),
  };
  const inner = shells[badge] || (
    <div className="p-6 text-center text-[#747680]">Preview</div>
  );
  return (
    <div className="vp-glass p-2">
      <div className="aspect-[16/10] rounded-xl overflow-hidden bg-gradient-to-br from-[#F6F7FA] to-white border border-[#E4E6EC]">
        {inner}
      </div>
    </div>
  );
}

// ============= i18n dictionaries =============
const I18N = {
  hi: {
    // Nav
    nav_login: "Log in",
    nav_signup: "Start free",
    nav_features: "Features",
    nav_stories: "Customer stories",
    nav_pricing: "Pricing",
    nav_blog: "Blog",
    nav_faq: "FAQ",
    // Hero
    hero_badge: "India ka #1 webinar-to-revenue OS · 1,200+ creators iske saath",
    hero_h1_line1: "Idea aapka.",
    hero_h1_line2_prefix: "Automation ",
    hero_h1_line2_suffix: " ka.",
    hero_desc_before: "Ek topic likho — landing page, Razorpay payment, Zoom, WhatsApp + email reminders, aur certificate ",
    hero_desc_bold: "apne aap ban jaate hain",
    hero_desc_after: ". Coaches, YouTubers aur creators ke liye — Hinglish mein.",
    hero_cta_primary: "Free me shuru karo — card nahi chahiye",
    hero_cta_secondary: "2 min me dekho",
    trust_whatsapp: "Meta-BSP verified WhatsApp",
    trust_india: "India-first · RBI-compliant",
    trust_uptime: "99.9% uptime SLA",
    // Ladder
    ladder_kicker: "Kaise kaam karta hai",
    ladder_h2_a: "5 steps. Zero setup. ",
    ladder_h2_b: "Pura autopilot.",
    ladder_desc: "Topic paste karo — landing page, payment, Zoom, reminders, certificate. Sab kuch ek flow me, ek dashboard me.",
    // Stats band
    stats_featured: "Featured in · Talked about on",
    stats_headline: "India ke creators iss platform pe chalte hain",
    stats_1: "creators automating",
    stats_2: "registrations processed",
    stats_3: "webinar attendees",
    stats_4: "automation rules built-in",
    // Compare
    compare_kicker: "Kyun switch karo",
    compare_h2_a: "Zoom Webinar ka ",
    compare_h2_b: "1/10th cost",
    compare_h2_c: ". 10x zyada automation.",
    compare_desc: "Zoom Webinar sirf hosting deta hai. Landing page, payment, WhatsApp reminders, certificates — sab manually banao. Videoepreneur sab kuch ek hi jagah, Indian pricing me.",
    compare_cta: "Free me shuru karo — no card",
    // Pricing
    pricing_kicker: "Pricing",
    pricing_h2: "Growth pe kharcho. Tools pe nahi.",
    pricing_desc: "Free se shuru karo. Webinar se paisa aane par upgrade — pehle nahi. 14 din ka money-back guarantee.",
    pricing_monthly: "Monthly",
    pricing_yearly: "Yearly",
    pricing_save: "Save 20%",
    pricing_yearly_hint: (save) => `Yearly me ₹${save.toLocaleString("en-IN")} save karo →`,
    pricing_yearly_badge: (save) => `₹${save.toLocaleString("en-IN")} saved/yr`,
    pricing_mo: "/mo",
    // FAQ
    faq_kicker: "FAQs",
    faq_h2: "Sawaal-jawaab. Bina chakkar ke.",
    faq_desc_a: "Aur bhi kuch puchna hai? ",
    faq_desc_b: " pe email karo — 4 ghante me reply.",
  },
  en: {
    nav_login: "Log in", nav_signup: "Start free",
    nav_features: "Features", nav_stories: "Customer stories",
    nav_pricing: "Pricing", nav_blog: "Blog", nav_faq: "FAQ",
    hero_badge: "India's #1 webinar-to-revenue OS · trusted by 1,200+ creators",
    hero_h1_line1: "Your idea.",
    hero_h1_line2_prefix: "Automation by ",
    hero_h1_line2_suffix: ".",
    hero_desc_before: "Type a topic — landing page, Razorpay payments, Zoom, WhatsApp + email reminders, and certificates ",
    hero_desc_bold: "build themselves",
    hero_desc_after: ". Built for coaches, YouTubers, and creators — Hinglish or English.",
    hero_cta_primary: "Start free — no card needed",
    hero_cta_secondary: "Watch 2-min tour",
    trust_whatsapp: "Meta-BSP verified WhatsApp",
    trust_india: "India-first · RBI-compliant",
    trust_uptime: "99.9% uptime SLA",
    ladder_kicker: "How it works",
    ladder_h2_a: "5 steps. Zero setup. ",
    ladder_h2_b: "Full autopilot.",
    ladder_desc: "Paste a topic — landing page, payment, Zoom, reminders, certificate. Everything in one flow, one dashboard.",
    stats_featured: "Featured in · Talked about on",
    stats_headline: "Creators across India run their business on this",
    stats_1: "creators automating",
    stats_2: "registrations processed",
    stats_3: "webinar attendees",
    stats_4: "automation rules built-in",
    compare_kicker: "Why switch",
    compare_h2_a: "Zoom Webinar at ",
    compare_h2_b: "1/10th the cost",
    compare_h2_c: ". 10x more automation.",
    compare_desc: "Zoom Webinar just hosts the meeting. Landing page, payments, WhatsApp reminders, certificates — you build all of that manually. Videoepreneur bundles it all with India-native pricing.",
    compare_cta: "Start free — no card",
    pricing_kicker: "Pricing",
    pricing_h2: "Pay for growth. Not for tools.",
    pricing_desc: "Start free. Upgrade only when your webinar earns you money — not before. 14-day money-back guarantee.",
    pricing_monthly: "Monthly",
    pricing_yearly: "Yearly",
    pricing_save: "Save 20%",
    pricing_yearly_hint: (save) => `Save ₹${save.toLocaleString("en-IN")} with yearly →`,
    pricing_yearly_badge: (save) => `₹${save.toLocaleString("en-IN")} saved/yr`,
    pricing_mo: "/mo",
    faq_kicker: "FAQs",
    faq_h2: "Real questions. Straight answers.",
    faq_desc_a: "Anything else on your mind? Email ",
    faq_desc_b: " — we reply in 4 hours.",
  },
  hn: {
    // Pure Devanagari Hindi
    nav_login: "लॉग इन", nav_signup: "फ्री शुरू करें",
    nav_features: "फीचर्स", nav_stories: "स्टोरीज़",
    nav_pricing: "प्राइसिंग", nav_blog: "ब्लॉग", nav_faq: "FAQ",
    hero_badge: "भारत का #1 वेबिनार-टू-रेवेन्यू OS · 1,200+ क्रिएटर्स के साथ",
    hero_h1_line1: "आपका आइडिया।",
    hero_h1_line2_prefix: "ऑटोमेशन ",
    hero_h1_line2_suffix: " का।",
    hero_desc_before: "एक टॉपिक लिखिए — लैंडिंग पेज, Razorpay पेमेंट, Zoom, WhatsApp + ईमेल रिमाइंडर, और सर्टिफिकेट ",
    hero_desc_bold: "अपने आप बन जाते हैं",
    hero_desc_after: "। कोच, यूट्यूबर और क्रिएटर्स के लिए — हिंदी में भी।",
    hero_cta_primary: "फ्री में शुरू करें — कार्ड की ज़रूरत नहीं",
    hero_cta_secondary: "2 मिनट में देखें",
    trust_whatsapp: "मेटा-BSP वेरिफाइड WhatsApp",
    trust_india: "इंडिया-फर्स्ट · RBI-कम्प्लायंट",
    trust_uptime: "99.9% अपटाइम SLA",
    ladder_kicker: "कैसे काम करता है",
    ladder_h2_a: "5 स्टेप्स। ज़ीरो सेटअप। ",
    ladder_h2_b: "पूरा ऑटोपायलट।",
    ladder_desc: "टॉपिक पेस्ट करिए — लैंडिंग, पेमेंट, Zoom, रिमाइंडर्स, सर्टिफिकेट। सब एक फ्लो में, एक डैशबोर्ड में।",
    stats_featured: "फीचर्ड इन · टॉक्ड अबाउट ऑन",
    stats_headline: "भारत के क्रिएटर्स इस प्लेटफ़ॉर्म पर चलते हैं",
    stats_1: "क्रिएटर्स ऑटोमेटिंग",
    stats_2: "रजिस्ट्रेशन प्रोसेस्ड",
    stats_3: "वेबिनार अटेंडीज़",
    stats_4: "ऑटोमेशन नियम बिल्ट-इन",
    compare_kicker: "स्विच क्यों करें",
    compare_h2_a: "Zoom Webinar का ",
    compare_h2_b: "1/10 खर्चा",
    compare_h2_c: "। 10x ज़्यादा ऑटोमेशन।",
    compare_desc: "Zoom Webinar सिर्फ़ मीटिंग होस्ट करता है। लैंडिंग, पेमेंट, WhatsApp रिमाइंडर, सर्टिफिकेट — सब मैन्युअली बनाना पड़ता है। Videoepreneur में सब एक जगह — इंडिया-नेटिव प्राइसिंग में।",
    compare_cta: "फ्री में शुरू करें — नो कार्ड",
    pricing_kicker: "प्राइसिंग",
    pricing_h2: "ग्रोथ पर ख़र्च करिए। टूल्स पर नहीं।",
    pricing_desc: "फ्री से शुरू करिए। जब वेबिनार से पैसे आने लगें, तभी अपग्रेड। 14 दिन का मनी-बैक।",
    pricing_monthly: "मासिक",
    pricing_yearly: "सालाना",
    pricing_save: "20% बचाएँ",
    pricing_yearly_hint: (save) => `सालाना में ₹${save.toLocaleString("en-IN")} बचाएँ →`,
    pricing_yearly_badge: (save) => `₹${save.toLocaleString("en-IN")} /वर्ष बचत`,
    pricing_mo: "/माह",
    faq_kicker: "FAQs",
    faq_h2: "सवाल-जवाब। बिना चक्कर के।",
    faq_desc_a: "कुछ और पूछना है? ",
    faq_desc_b: " पर ईमेल करें — 4 घंटे में जवाब।",
  },
};

// ============= Component =============
// A tiny floating "Host login" pill for custom-domain landing pages. Keeps
// tenant branding pure (no Videoepreneur chrome) while still giving the
// workspace owner + attendees a discoverable way to reach `/login`.
function TenantLoginFab({ accent = "#0B5CFF" }) {
  return (
    <a
      href="/login"
      data-testid="tenant-login-fab"
      className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-white text-sm font-semibold shadow-xl hover:opacity-90 transition-opacity"
      style={{ backgroundColor: accent }}
    >
      Host login →
    </a>
  );
}

export default function MarketingHome() {
  useSeo({
    title: "Videoepreneur — Webinar-to-Revenue Automation for Creators & Coaches",
    description: "India's #1 webinar automation OS. Landing pages, payments, Email + WhatsApp reminders, AI content — everything you need to go from idea to paid webinar in one afternoon.",
    image: "https://videoepreneur.com/og-cover.png",
  });
  const [openFaq, setOpenFaq] = useState(0);
  const [yearly, setYearly] = useState(true);
  const [demoOpen, setDemoOpen] = useState(false);
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem("vp_lang");
      if (stored && ["hi", "en", "hn"].includes(stored)) return stored;
      // Auto-detect on first visit
      const nav = navigator.language || navigator.userLanguage || "en";
      const nl = nav.toLowerCase();
      // Devanagari Hindi speakers (hi-IN with actual Hindi input) — default to Hinglish to be safe
      // but browser locale of 'hi', 'hi-IN' → Hinglish since that's most India-friendly
      if (nl.startsWith("hi")) return "hi";
      // Timezone-based Indian audience → Hinglish
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") return "hi";
      } catch {}
      // Non-India → English
      return "en";
    } catch { return "hi"; }
  });
  useEffect(() => { try { localStorage.setItem("vp_lang", lang); } catch {} }, [lang]);
  const t = (key) => (I18N[lang] && I18N[lang][key]) || I18N.hi[key] || key;
  const [scrolled, setScrolled] = useState(false);
  const [primaryPage, setPrimaryPage] = useState(null);
  const [tenantHost, setTenantHost] = useState(null);   // set when the current host IS a custom domain
  const [tenantState, setTenantState] = useState(null); // "live" | "no_primary_page" | "pending"
  const [checking, setChecking] = useState(true);

  // ---- Custom domain detection ----
  // If the visitor is on a workspace's custom domain AND that domain has a primary
  // landing page linked, render THAT page. If the domain is registered but not
  // fully configured yet, render a friendly "being set up" screen — NEVER fall
  // back to the Videoepreneur platform site on a customer domain.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        let platformHost = "";
        try { platformHost = new URL(process.env.REACT_APP_BACKEND_URL).host; } catch { /* noop */ }
        const currentHost = window.location.host;
        const isCustomDomain = platformHost && currentHost && currentHost !== platformHost;
        if (!isCustomDomain) { setChecking(false); return; }
        setTenantHost(currentHost);
        const { data } = await axios.get(`${API_BASE}/public/domain-lookup`, { params: { host: currentHost } });
        if (cancel) return;
        setTenantState(data?.state || (data?.primary_page ? "live" : "no_primary_page"));
        if (data?.primary_page) {
          if (data.primary_page.seo_title) document.title = data.primary_page.seo_title;
          setPrimaryPage(data.primary_page);
        }
      } catch (err) {
        // 404 = domain not registered at all — treat as pending so we still
        // don't leak the platform site on someone else's hostname.
        if (!cancel && err?.response?.status === 404) setTenantState("pending");
      }
      finally { if (!cancel) setChecking(false); }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // While probing the host we render nothing to avoid a flash of the wrong page
  if (checking) return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading…</div>;

  // Custom-domain visitor with a linked primary page → white-label render
  if (primaryPage) {
    // Expose the active page slug so nested components (WaitlistFormSection) can POST to /public/pages/{slug}/…
    if (typeof window !== "undefined") window.__vp_active_page_slug = primaryPage.slug;
    return (
      <div>
        {primaryPage.sections.map((s) => (
          <SectionView key={s.id} section={s} accent={primaryPage.accent_color} />
        ))}
        <TenantLoginFab accent={primaryPage.accent_color} />
      </div>
    );
  }

  // Custom domain but not (yet) fully configured — show a neutral holding page
  // instead of the Videoepreneur marketing site. This prevents the platform
  // brand from leaking onto the customer's hostname.
  if (tenantHost) {
    const isPending = tenantState === "pending";
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-[#F6F7FA] to-[#E6EEFF] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#0B5CFF]/10 flex items-center justify-center mb-5">
            <div className="w-6 h-6 rounded-full border-2 border-[#0B5CFF] border-t-transparent animate-spin" />
          </div>
          <h1 className="font-display font-black text-2xl text-[#0B0D17]">
            {isPending ? "Setting things up…" : "Almost ready"}
          </h1>
          <p className="text-sm text-[#4A4D57] mt-3 leading-relaxed">
            {isPending
              ? `${tenantHost} is being provisioned. DNS + SSL usually finish within a few minutes.`
              : `${tenantHost} is connected but the owner hasn't picked a landing page yet.`}
          </p>
          <p className="text-xs text-[#747680] mt-6">Please check back in a little while.</p>
          <div className="mt-8">
            <a href="/login" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B5CFF] hover:underline">
              Host login →
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-[#050B24]">
      {/* Sticky Nav */}
      <nav className={"sticky top-0 z-40 transition-all " + (scrolled ? "bg-white/95 backdrop-blur-md border-b border-[#E4E6EC] text-[#0B0D17]" : "bg-transparent text-white")}>
        <div className="max-w-[1280px] mx-auto flex items-center justify-between px-5 sm:px-8 py-4">
          <Link to="/" className={"flex items-center gap-2 " + (scrolled ? "text-[#0B0D17]" : "text-white")}>
            <div className="w-9 h-9 rounded-xl bg-[#0B5CFF] flex items-center justify-center">
              <Video className="w-5 h-5 text-white" strokeWidth={1.75} />
            </div>
            <span className="font-display font-semibold text-lg">Videoepreneur</span>
          </Link>
          <div className={"hidden md:flex items-center gap-8 text-sm " + (scrolled ? "text-[#4A4D57]" : "text-white/85")}>
            <a href="#features" className={scrolled ? "hover:text-[#0B0D17]" : "hover:text-white"}>{t("nav_features")}</a>
            <a href="#stories" className={scrolled ? "hover:text-[#0B0D17]" : "hover:text-white"}>{t("nav_stories")}</a>
            <a href="#pricing" className={scrolled ? "hover:text-[#0B0D17]" : "hover:text-white"}>{t("nav_pricing")}</a>
            <Link to="/blog" className={scrolled ? "hover:text-[#0B0D17]" : "hover:text-white"} data-testid="marketing-nav-blog">{t("nav_blog")}</Link>
            <a href="#faq" className={scrolled ? "hover:text-[#0B0D17]" : "hover:text-white"}>{t("nav_faq")}</a>
          </div>
          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div
              className={"hidden sm:inline-flex items-center gap-0.5 p-0.5 rounded-full text-[11px] font-bold border " + (scrolled ? "bg-[#F1F3F7] border-[#E4E6EC]" : "bg-white/10 border-white/20 backdrop-blur-sm")}
              data-testid="marketing-lang-switcher"
            >
              {[
                { c: "hi", l: "HI" },
                { c: "en", l: "EN" },
                { c: "hn", l: "हिं" },
              ].map((o) => {
                const active = lang === o.c;
                return (
                  <button
                    key={o.c}
                    type="button"
                    onClick={() => setLang(o.c)}
                    data-testid={`lang-${o.c}`}
                    className={"px-2.5 py-1 rounded-full transition-all " +
                      (active
                        ? (scrolled ? "bg-white text-[#0B0D17] shadow" : "bg-white text-[#0B0D17]")
                        : (scrolled ? "text-[#747680] hover:text-[#0B0D17]" : "text-white/70 hover:text-white"))}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
            <Link
              to="/login"
              data-testid={TIDS.marketingNavLogin}
              className={"text-sm hidden sm:inline-flex px-4 py-2 rounded-full font-semibold transition-colors " + (scrolled ? "text-[#0B0D17] hover:bg-[#F1F3F7]" : "text-white hover:bg-white/10")}
            >
              {t("nav_login")}
            </Link>
            <Link to="/signup" data-testid={TIDS.marketingNavSignup} className={"text-sm px-4 py-2 rounded-full font-semibold transition-all " + (scrolled ? "bg-[#0B5CFF] text-white hover:bg-[#0A4FD9]" : "bg-white text-[#050B24] hover:scale-[1.03]")}>
              {t("nav_signup")}
            </Link>
          </div>
        </div>
      </nav>

      {/* ============ Hero (dark, centered, big mockup below) ============ */}
      <section className="relative overflow-hidden bg-[#050B24] text-white" data-testid="marketing-hero">
        {/* Ambient glows */}
        <div className="absolute inset-0 pointer-events-none opacity-70" style={{
          background: "radial-gradient(circle at 20% 0%, rgba(11,92,255,0.35), transparent 55%), radial-gradient(circle at 80% 10%, rgba(124,58,237,0.30), transparent 55%), radial-gradient(circle at 50% 100%, rgba(11,92,255,0.20), transparent 60%)"
        }} />
        {/* Subtle horizontal light beams */}
        <div className="absolute inset-x-0 top-[45%] h-32 pointer-events-none opacity-40" style={{
          background: "linear-gradient(180deg, transparent, rgba(120,150,255,0.20), transparent)",
          filter: "blur(30px)",
        }} />

        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-8 sm:pb-12 text-center vp-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs backdrop-blur-sm">
            <Rocket className="w-3.5 h-3.5 text-[#22D3EE]" strokeWidth={1.75} />
            <span className="text-white/80">{t("hero_badge")}</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-6 max-w-5xl mx-auto">
            <span className="block">{t("hero_h1_line1")}</span>
            <span className="block">
              {t("hero_h1_line2_prefix")}
              <span className="bg-gradient-to-r from-[#7BB8FF] via-[#4C8BFF] to-[#7BB8FF] bg-clip-text text-transparent">
                Videoepreneur
              </span>
              {t("hero_h1_line2_suffix")}
            </span>
          </h1>
          <p className="text-white/70 text-base sm:text-lg mt-6 leading-relaxed max-w-2xl mx-auto">
            {t("hero_desc_before")}<span className="text-white font-medium">{t("hero_desc_bold")}</span>{t("hero_desc_after")}
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/signup"
              data-testid={TIDS.marketingCtaHero}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#050B24] font-semibold text-sm sm:text-base hover:scale-[1.02] transition-transform"
            >
              {t("hero_cta_primary")} <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0B5CFF] text-white font-semibold text-sm sm:text-base hover:bg-[#0A4FD9] transition-colors"
              data-testid="marketing-demo-open"
            >
              <PlayCircle className="w-4 h-4" /> {t("hero_cta_secondary")}
            </button>
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-5 text-xs text-white/50">
            <div className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#22D3EE]" /> {t("trust_whatsapp")}</div>
            <div className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-[#22D3EE]" /> {t("trust_india")}</div>
            <div className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[#22D3EE]" /> {t("trust_uptime")}</div>
          </div>
        </div>

        {/* BIG centered mockup video */}
        <div className="relative max-w-[1400px] mx-auto px-3 sm:px-6 pb-16 sm:pb-24" data-testid="hero-mockup-video-wrap">
          <div className="relative rounded-2xl overflow-hidden shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)] border border-white/10 bg-[#0B0D17]">
            <video
              src="/videos/hero.mp4"
              poster="/videos/hero-poster.jpg"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              className="block w-full h-auto"
              aria-label="Videoepreneur webinar interface preview"
              data-testid="hero-mockup-video"
            />
            {/* Soft top+bottom gradient glow to blend with the dark hero */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/25 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/25 to-transparent" />
          </div>
        </div>

        {/* Live activity ticker — centered chip below mockup */}
        <div className="relative pb-14 sm:pb-20 flex justify-center px-5">
          <LiveTicker />
        </div>
      </section>

      {/* ============ Sequential value ladder (moved out of hero) ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="vp-label" style={{ color: "#0B5CFF" }}>{t("ladder_kicker")}</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-tight">
              {t("ladder_h2_a")}<span className="text-[#0B5CFF]">{t("ladder_h2_b")}</span>
            </h2>
            <p className="text-[#4A4D57] text-base sm:text-lg mt-4 leading-relaxed">
              {t("ladder_desc")}
            </p>
          </div>

          <div className="mt-12 max-w-3xl mx-auto space-y-2.5" data-testid="hero-value-ladder">
            {[
              { n: "01", label: "Landing Page", body: "AI headline + copy + agenda in 40 sec", dur: "~40 SEC", accent: "#0B5CFF" },
              { n: "02", label: "Razorpay Payment", body: "UPI · cards · GST invoice · coupons", dur: "1 CLICK", accent: "#059669" },
              { n: "03", label: "Zoom Auto-setup", body: "Shared license · per-attendee tracked links", dur: "0 SETUP", accent: "#7C3AED" },
              { n: "04", label: "Reminders", body: "WhatsApp + email at 24h · 1h · 10min", dur: "AUTOPILOT", accent: "#EA580C" },
              { n: "05", label: "Certificate + Recording", body: "Personalised PDF + recording auto-delivered", dur: "AUTO", accent: "#DC2626" },
            ].map((s, i) => (
              <div
                key={s.n}
                className="group relative flex items-center gap-4 vp-card p-4 hover:border-[#0B5CFF]/60 hover:shadow-[0_4px_16px_rgba(11,92,255,0.08)] transition-all"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`hero-step-${s.n}`}
              >
                <div
                  className="w-12 h-12 shrink-0 rounded-lg font-mono font-black text-base flex items-center justify-center"
                  style={{ background: s.accent + "15", color: s.accent }}
                >
                  {s.n}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-base sm:text-lg leading-snug">{s.label}</div>
                  <div className="text-sm text-[#4A4D57] mt-0.5">{s.body}</div>
                </div>
                <div
                  className="text-[10px] font-mono font-bold px-2.5 py-1.5 rounded-md shrink-0 hidden sm:block"
                  style={{ background: s.accent + "12", color: s.accent, letterSpacing: "0.06em" }}
                >
                  {s.dur}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Stats band (social proof) ============ */}
      <section className="border-y border-[#E4E6EC] bg-gradient-to-r from-[#F6F7FA] via-white to-[#F6F7FA] py-10">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          {/* Featured in band */}
          <div className="mb-8" data-testid="marketing-featured-in">
            <div className="text-center vp-label mb-4" style={{ color: "#747680" }}>{t("stats_featured")}</div>
            <div className="flex items-center justify-center gap-6 sm:gap-10 flex-wrap">
              {[
                { name: "Product Hunt", tag: "#1 Product of the Day", icon: "🎯", color: "#DA552F" },
                { name: "IndieHackers", tag: "Milestone story", icon: "💡", color: "#0E2439" },
                { name: "YourStory", tag: "India creator OS", icon: "📰", color: "#EE1B24" },
                { name: "TechCrunch India", tag: "Pre-seed spotlight", icon: "🚀", color: "#00A82D" },
                { name: "Twitter/X", tag: "12K+ mentions", icon: "𝕏", color: "#0B0D17" },
              ].map((b) => (
                <div key={b.name} className="inline-flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
                  <span className="text-xl leading-none" style={{ color: b.color }}>{b.icon}</span>
                  <div className="text-left">
                    <div className="text-sm font-display font-bold text-[#0B0D17] leading-tight">{b.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#747680] font-semibold leading-tight mt-0.5">{b.tag}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-[#E4E6EC] pt-8 text-center vp-label mb-6" style={{ color: "#0B5CFF" }}>{t("stats_headline")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="marketing-stats-band">
            {[
              { n: "1,200+", label: t("stats_1") },
              { n: "₹2Cr+", label: t("stats_2") },
              { n: "25K+", label: t("stats_3") },
              { n: "11", label: t("stats_4") },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#0B0D17] tracking-tight leading-none">{s.n}</div>
                <div className="text-xs sm:text-sm text-[#4A4D57] mt-2">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-center gap-6 sm:gap-10 flex-wrap opacity-60">
            {["EduLoft", "CreatorClub", "MentorHive", "SkillsRupee", "WebinarBharat", "CohortStack"].map((name) => (
              <div key={name} className="font-display font-black text-base sm:text-lg text-[#747680] tracking-tight">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Chip marquee — capability strip ============ */}
      <section className="py-8 bg-[#0B0D17] text-white overflow-hidden" data-testid="marketing-chip-marquee">
        <div className="flex gap-3 animate-vp-marquee-left whitespace-nowrap" style={{ width: "max-content" }}>
          {[
            ...Array(3).fill([
              { label: "LANDING PAGE", icon: "🖥️", color: "#0B5CFF" },
              { label: "RAZORPAY PAYMENT", icon: "💳", color: "#059669" },
              { label: "ZOOM AUTO-SETUP", icon: "🎥", color: "#7C3AED" },
              { label: "WHATSAPP REMINDERS", icon: "💬", color: "#25D366" },
              { label: "EMAIL DRIP", icon: "📧", color: "#EA580C" },
              { label: "AI CONTENT (HINGLISH)", icon: "✨", color: "#F0ABFC" },
              { label: "CUSTOM DOMAIN", icon: "🌐", color: "#22D3EE" },
              { label: "CERTIFICATES PDF", icon: "🏆", color: "#F5A623" },
              { label: "COHORTS + COURSES", icon: "🎓", color: "#EC4899" },
              { label: "LIVE Q&A + AI CO-PILOT", icon: "🎙️", color: "#F0ABFC" },
              { label: "ANALYTICS FUNNEL", icon: "📊", color: "#22D3EE" },
              { label: "THUMBNAIL STUDIO", icon: "🎨", color: "#DC2626" },
            ]).flat(),
          ].map((c, i) => (
            <div
              key={i}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm"
            >
              <span className="text-base leading-none">{c.icon}</span>
              <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color: c.color }}>{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ============ Comparison table ============ */}
      <section id="compare" className="py-24 bg-[#0B0D17] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 20%, rgba(11,92,255,0.4), transparent 60%), radial-gradient(circle at 80% 80%, rgba(124,58,237,0.35), transparent 55%)" }} />
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 relative">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-widest font-semibold text-[#F0ABFC]">
              <TrendingUp className="w-3.5 h-3.5" /> {t("compare_kicker")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tight leading-tight">
              {t("compare_h2_a")}<span className="text-[#F0ABFC]">{t("compare_h2_b")}</span>{t("compare_h2_c")}
            </h2>
            <p className="text-white/70 text-base mt-5 leading-relaxed">
              {t("compare_desc")}
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6" data-testid="marketing-compare-table">
            {/* Zoom col */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 relative">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Alternative</div>
                  <div className="font-display font-black text-2xl mt-1">Zoom Webinar</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Starts at</div>
                  <div className="font-display font-black text-2xl text-white/80">₹15,000<span className="text-sm font-normal">/mo</span></div>
                </div>
              </div>
              <ul className="space-y-3.5 text-sm">
                {[
                  { label: "Landing page", val: "Kuch nahi — Wix / WordPress alag" },
                  { label: "Payment", val: "Stripe / Razorpay alag setup" },
                  { label: "WhatsApp reminders", val: "Manually broadcast karo" },
                  { label: "Email drip", val: "Mailchimp / ActiveCampaign extra" },
                  { label: "Certificates", val: "Canva me haath se banao" },
                  { label: "AI content", val: "ChatGPT copy-paste karo" },
                  { label: "Custom domain", val: "Enterprise plan +₹40K/mo" },
                  { label: "Analytics", val: "Basic — CSV export bas" },
                ].map((r) => (
                  <li key={r.label} className="flex gap-3">
                    <span className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-red-300 text-[13px] leading-none">✕</span>
                    </span>
                    <div>
                      <div className="text-white/60 text-xs uppercase tracking-wider">{r.label}</div>
                      <div className="text-white/85 mt-0.5">{r.val}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-white/10 text-xs text-white/50">
                Total real cost: ₹40K–₹90K/month across 4-5 tools
              </div>
            </div>

            {/* Videoepreneur col */}
            <div className="rounded-2xl border-2 border-[#0B5CFF]/60 bg-gradient-to-br from-[#0B5CFF]/10 to-[#7C3AED]/10 p-6 relative shadow-[0_0_60px_rgba(11,92,255,0.25)]">
              <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-gradient-to-r from-[#0B5CFF] to-[#7C3AED] text-white text-[10px] font-bold uppercase tracking-widest">
                Recommended
              </div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#F0ABFC] font-semibold">Videoepreneur</div>
                  <div className="font-display font-black text-2xl mt-1 flex items-center gap-2">
                    <Video className="w-5 h-5 text-[#F0ABFC]" /> All-in-one
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#F0ABFC]/70">Starts at</div>
                  <div className="font-display font-black text-2xl text-white">₹1,499<span className="text-sm font-normal">/mo</span></div>
                </div>
              </div>
              <ul className="space-y-3.5 text-sm">
                {[
                  { label: "Landing page", val: "AI writes in 40 sec — publish on your domain" },
                  { label: "Payment", val: "Razorpay UPI + cards + GST invoice built-in" },
                  { label: "WhatsApp reminders", val: "Meta BSP · 24h · 1h · 10min automated" },
                  { label: "Email drip", val: "11 rules pre-built, visual builder included" },
                  { label: "Certificates", val: "Personalised PDF auto-delivered" },
                  { label: "AI content", val: "Kimi + Claude Sonnet 4.5 in Hinglish" },
                  { label: "Custom domain", val: "Pro plan · CNAME + auto-SSL" },
                  { label: "Analytics", val: "Funnel + UTM + revenue by hour" },
                ].map((r) => (
                  <li key={r.label} className="flex gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#22D3EE]/25 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#22D3EE]" strokeWidth={3} />
                    </span>
                    <div>
                      <div className="text-[#F0ABFC]/80 text-xs uppercase tracking-wider">{r.label}</div>
                      <div className="text-white mt-0.5 font-medium">{r.val}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-white/15">
                <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#F0ABFC] to-[#22D3EE] text-[#0B0D17] font-bold text-sm hover:scale-[1.02] transition-transform" data-testid="marketing-compare-cta">
                  {t("compare_cta")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center text-sm text-white/60">
            <span className="font-semibold text-white">Real math:</span> Zoom Webinar (₹15K) + Wix (₹800) + Mailchimp (₹2K) + WhatsApp BSP (₹5K) + Canva Pro (₹500) = <span className="text-white font-bold">₹23,300/mo</span>. <br className="hidden sm:block" />
            Videoepreneur Pro (₹1,499/mo) me sab kuch. <span className="text-[#22D3EE] font-semibold">93% saving.</span>
          </div>
        </div>
      </section>

      {/* ============ Persona use-cases ============ */}
      <section className="py-24 bg-[#FBFBFD]">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="vp-label" style={{ color: "#0B5CFF" }}>Tumhare jaise creators ke liye</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-tight">
              Jo bhi banate ho, <br className="hidden sm:block" />
              workflow ready hai.
            </h2>
            <p className="text-[#4A4D57] text-lg mt-4 leading-relaxed">
              Har persona ke liye pre-built templates + automations. Bina team ke, bina designer ke, bina dev ke.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-14" data-testid="marketing-persona-cards">
            {[
              {
                emoji: "🎓",
                title: "Coach / Trainer",
                sub: "Cohort bootcamps, weekly workshops, live Q&A calls.",
                bullets: ["₹4.9K cohort me ₹80K/month recurring", "WhatsApp reminders = 71% attendance"],
                stat: "+142%",
                statLabel: "revenue per launch",
                gradient: "from-[#0B5CFF]/10 to-[#22D3EE]/10",
                accent: "#0B5CFF",
              },
              {
                emoji: "🎬",
                title: "YouTuber / Creator",
                sub: "Audience ko paid workshop me convert karo.",
                bullets: ["AI landing page 40 sec me publish", "Payment + Zoom ek hi link me"],
                stat: "212",
                statLabel: "seats first evening",
                gradient: "from-[#7C3AED]/10 to-[#EC4899]/10",
                accent: "#7C3AED",
              },
              {
                emoji: "💼",
                title: "Consultant / Coach",
                sub: "1-on-1 consultations aur monthly masterclasses.",
                bullets: ["Certificate PDF automatic", "GST invoice + coupon system built-in"],
                stat: "−12h",
                statLabel: "ops time / week",
                gradient: "from-[#059669]/10 to-[#22D3EE]/10",
                accent: "#059669",
              },
              {
                emoji: "🏫",
                title: "Coaching Institute",
                sub: "Multi-teacher workspaces, cohort-based courses.",
                bullets: ["Team seats + role permissions", "White-label on your own domain"],
                stat: "5x",
                statLabel: "cohorts per month",
                gradient: "from-[#EA580C]/10 to-[#F5A623]/10",
                accent: "#EA580C",
              },
            ].map((p) => (
              <div
                key={p.title}
                className={"vp-card p-6 bg-gradient-to-br " + p.gradient + " hover:shadow-lg hover:-translate-y-1 transition-all"}
                data-testid={"persona-" + p.title.toLowerCase().split(" ")[0]}
              >
                <div className="text-4xl leading-none">{p.emoji}</div>
                <div className="font-display font-black text-xl mt-4">{p.title}</div>
                <p className="text-sm text-[#4A4D57] mt-2 leading-snug">{p.sub}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-[#0B0D17]">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: p.accent }} strokeWidth={2.5} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-black/5">
                  <div className="font-display font-black text-3xl" style={{ color: p.accent }}>{p.stat}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#747680] font-semibold mt-0.5">{p.statLabel}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Feature deep-dives ============ */}
      <section id="features" className="py-24 text-white">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="vp-label text-[#22D3EE]">Product tour</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-tight text-white">
              Six things that actually move your revenue.
            </h2>
            <p className="text-white/70 text-lg mt-5 leading-relaxed">
              Every feature is designed for the Indian creator's reality — mobile-first attendees, WhatsApp-native comms, INR payments, and last-minute registrations.
            </p>
          </div>

          <div className="mt-16 space-y-24">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              const reverse = i % 2 === 1;
              return (
                <div key={f.badge} className={"grid lg:grid-cols-12 gap-10 items-center " + (reverse ? "lg:[direction:rtl]" : "")}>
                  <div className="lg:col-span-6 vp-fade-up [direction:ltr]">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#22D3EE]/15 text-[#22D3EE] border border-[#22D3EE]/30 text-[11px] font-bold uppercase tracking-wider">
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {f.badge}
                    </div>
                    <h3 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl mt-4 leading-tight tracking-tight text-white">
                      {f.title}
                    </h3>
                    <p className="text-white/70 text-base mt-4 leading-relaxed max-w-xl">{f.body}</p>
                    <ul className="mt-6 space-y-2.5">
                      {f.bullets.map((b, k) => (
                        <li key={k} className="flex items-start gap-2.5 text-sm text-white/85">
                          <Check className="w-4 h-4 text-[#22C55E] mt-0.5 shrink-0" strokeWidth={2.25} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="lg:col-span-6 [direction:ltr]">
                    <FeatureMockup badge={f.badge} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ Case studies ============ */}
      <section id="stories" className="py-24 bg-[#F6F7FA] border-y border-[#E4E6EC]">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="vp-label">Customer stories</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-tight">
              Real creators. Real numbers.
            </h2>
            <p className="text-[#4A4D57] text-lg mt-5 leading-relaxed">
              These stories aren't polished for a pitch deck. They're how it actually plays out on Videoepreneur.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {CASES.map((c) => (
              <div key={c.name} className="vp-card vp-card-hover p-6 flex flex-col">
                <div className="flex items-center gap-3">
                  <img src={c.img} alt={c.name} className="w-12 h-12 rounded-full object-cover" loading="lazy" />
                  <div>
                    <div className="font-display font-semibold text-sm">{c.name}</div>
                    <div className="text-xs text-[#747680]">{c.role}</div>
                  </div>
                </div>
                <div className="mt-5 rounded-xl bg-gradient-to-br from-[#0B5CFF]/8 to-[#0B5CFF]/2 p-4 border border-[#0B5CFF]/15">
                  <div className="text-xs uppercase font-bold tracking-widest text-[#0B5CFF]">{c.metric_label}</div>
                  <div className="font-display font-black text-4xl text-[#0B5CFF] mt-1 tracking-tight">{c.metric_value}</div>
                </div>
                <p className="text-sm text-[#4A4D57] mt-4 leading-relaxed">{c.story}</p>
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {c.tags.map((t) => (
                    <span key={t} className="vp-chip text-xs">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Testimonial marquee (auto-scrolling) ============ */}
      <section className="py-24 bg-[#FBFBFD] overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="vp-label" style={{ color: "#0B5CFF" }}>Creators ki zubaani</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-tight">
              Jo roz iske saath revenue banate hain.
            </h2>
            <p className="text-[#4A4D57] text-lg mt-4 leading-relaxed">
              Coaches, YouTubers aur teachers — bina team ke bhi. Yeh raha unke actual results.
            </p>
          </div>
        </div>

        {/* Marquee row 1 (left → right) */}
        <div className="mt-14 relative" data-testid="marketing-testimonial-marquee">
          <div className="flex gap-4 animate-vp-marquee-left" style={{ width: "max-content" }}>
            {[...TESTI, ...TESTI, ...TESTI].map((t, i) => (
              <div key={"row1-" + i} className="w-[340px] shrink-0 vp-card p-5">
                <div className="flex text-[#F5A623]">
                  {[0, 1, 2, 3, 4].map((k) => <Star key={k} className="w-4 h-4" fill="currentColor" strokeWidth={0} />)}
                </div>
                <p className="text-[#0B0D17] mt-3 font-display text-base leading-snug">"{t.q}"</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0B5CFF] to-[#7C3AED] flex items-center justify-center text-white font-bold text-xs">
                    {t.n.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B0D17] text-xs">{t.n}</div>
                    <div className="text-[#747680] text-[10px]">{t.r}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* fade edges */}
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#FBFBFD] to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#FBFBFD] to-transparent pointer-events-none" />
        </div>

        {/* Marquee row 2 (right → left, slower) */}
        <div className="mt-4 relative">
          <div className="flex gap-4 animate-vp-marquee-right" style={{ width: "max-content" }}>
            {[...TESTI, ...TESTI, ...TESTI].reverse().map((t, i) => (
              <div key={"row2-" + i} className="w-[340px] shrink-0 vp-card p-5">
                <div className="flex text-[#F5A623]">
                  {[0, 1, 2, 3, 4].map((k) => <Star key={k} className="w-4 h-4" fill="currentColor" strokeWidth={0} />)}
                </div>
                <p className="text-[#0B0D17] mt-3 font-display text-base leading-snug">"{t.q}"</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center text-white font-bold text-xs">
                    {t.n.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B0D17] text-xs">{t.n}</div>
                    <div className="text-[#747680] text-[10px]">{t.r}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#FBFBFD] to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#FBFBFD] to-transparent pointer-events-none" />
        </div>
      </section>

      {/* ============ Pricing ============ */}
      <section id="pricing" className="py-24 vp-hero-bg">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="vp-label">{t("pricing_kicker")}</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-tight">
              {t("pricing_h2")}
            </h2>
            <p className="text-[#4A4D57] text-lg mt-5 leading-relaxed">
              {t("pricing_desc")}
            </p>

            {/* Monthly/Yearly toggle */}
            <div className="mt-8 inline-flex items-center gap-2 p-1 rounded-full bg-white border border-[#E4E6EC] shadow-sm" data-testid="pricing-toggle">
              <button
                type="button"
                onClick={() => setYearly(false)}
                data-testid="pricing-monthly"
                className={"px-5 py-2 rounded-full text-sm font-semibold transition-all " + (!yearly ? "bg-[#0B0D17] text-white shadow" : "text-[#4A4D57] hover:text-[#0B0D17]")}
              >
                {t("pricing_monthly")}
              </button>
              <button
                type="button"
                onClick={() => setYearly(true)}
                data-testid="pricing-yearly"
                className={"px-5 py-2 rounded-full text-sm font-semibold transition-all inline-flex items-center gap-2 " + (yearly ? "bg-[#0B0D17] text-white shadow" : "text-[#4A4D57] hover:text-[#0B0D17]")}
              >
                {t("pricing_yearly")}
                <span className={"text-[10px] px-1.5 py-0.5 rounded-full font-bold " + (yearly ? "bg-[#22D3EE] text-[#0B0D17]" : "bg-[#22D3EE]/20 text-[#059669]")}>{t("pricing_save")}</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-14 max-w-5xl mx-auto">
            {PRICING.map((p) => {
              const monthlyPrice = p.price;
              // yearly: 20% off, shown as effective monthly rate
              const yearlyEffective = Math.round(monthlyPrice * 0.8);
              const displayPrice = p.price === 0 ? 0 : (yearly ? yearlyEffective : monthlyPrice);
              return (
                <div key={p.key} className={"vp-glass p-6 flex flex-col relative transition-all " +
                  (p.popular ? "border-[#0B5CFF] shadow-[0_18px_44px_rgba(11,92,255,0.18)] lg:scale-105" : "")}>
                  {p.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 vp-chip vp-chip-accent">Most popular</div>
                  )}
                  <div className="font-display font-bold text-2xl">{p.label}</div>
                  <div className="mt-4">
                    <div className="flex items-baseline gap-1">
                      <span className="font-display font-black text-5xl tracking-tight">
                        {p.price === 0 ? "Free" : `₹${displayPrice.toLocaleString("en-IN")}`}
                      </span>
                      {p.price > 0 && <span className="text-[#747680] text-sm">{t("pricing_mo")}</span>}
                    </div>
                    {p.price > 0 && yearly && (
                      <div className="mt-1.5 flex items-center gap-2 text-xs">
                        <span className="line-through text-[#747680]">₹{monthlyPrice.toLocaleString("en-IN")}{t("pricing_mo")}</span>
                        <span className="px-1.5 py-0.5 rounded-md bg-[#22D3EE]/15 text-[#059669] font-bold">{t("pricing_yearly_badge")((monthlyPrice - yearlyEffective) * 12)}</span>
                      </div>
                    )}
                    {p.price > 0 && !yearly && (
                      <div className="mt-1.5 text-xs text-[#747680]">{t("pricing_yearly_hint")((monthlyPrice - Math.round(monthlyPrice * 0.8)) * 12)}</div>
                    )}
                  </div>
                  <ul className="mt-6 space-y-2.5 text-sm flex-1">
                    {p.perks.map((perk, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#0B0D17]">
                        <Check className="w-4 h-4 text-[#1E8E3E] mt-0.5" strokeWidth={2.25} />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/signup" data-testid={"pricing-cta-" + p.key}
                    className={"mt-6 " + (p.popular ? "vp-btn-primary" : "vp-btn-ghost")}>
                    {p.cta}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section id="faq" className="py-24 text-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="vp-label text-[#22D3EE]">{t("faq_kicker")}</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-tight text-white">
              {t("faq_h2")}
            </h2>
            <p className="text-white/70 text-base mt-4">{t("faq_desc_a")}<a href="mailto:hello@videoepreneur.com" className="text-[#22D3EE] font-semibold hover:underline">hello@videoepreneur.com</a>{t("faq_desc_b")}</p>
          </div>
          <div className="mt-12 space-y-3">
            {FAQ.map((item, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="vp-card overflow-hidden text-[#0B0D17]" data-testid={"faq-item-" + i}>
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-start justify-between gap-4 p-5 text-left"
                  >
                    <span className="font-display font-semibold text-lg leading-snug">{item.q}</span>
                    <ChevronDown className={"w-5 h-5 text-[#4A4D57] mt-1 shrink-0 transition-transform " + (open ? "rotate-180 text-[#0B5CFF]" : "")} strokeWidth={1.75} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 text-[#4A4D57] text-sm leading-relaxed border-t border-[#E4E6EC] pt-4">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ Final CTA ============ */}
      <section className="py-24 bg-[#0B0D17] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(11,92,255,0.6) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(11,92,255,0.4) 0%, transparent 45%)"
        }} />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center relative">
          <Gift className="w-10 h-10 text-[#0B5CFF] mx-auto mb-6" strokeWidth={1.25} />
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white">
            Agla webinar project nahi — ek click hai.
          </h2>
          <p className="text-white/70 text-lg mt-5 leading-relaxed max-w-xl mx-auto">
            Free se shuru karo. Pehli 100 registrations Videoepreneur ki taraf se. Card nahi chahiye. Lock-in nahi.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/signup" data-testid={TIDS.marketingCtaFinal} className="vp-btn-primary text-base">
              Free me shuru karo <ArrowRight className="w-4 h-4 ml-1 inline" />
            </Link>
            <a href="#pricing" className="px-5 py-3 rounded-full border border-white/30 text-white/90 hover:bg-white/10 text-sm font-semibold inline-flex items-center gap-2">
              Pricing dekho
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-6 text-xs text-white/50">
            <div className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Meta BSP verified</div>
            <div className="inline-flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> India ka #1 cohort platform</div>
          </div>
        </div>
      </section>

      {/* ============ Footer ============ */}
      <footer className="bg-white border-t border-[#E4E6EC]">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-[#0B5CFF] flex items-center justify-center">
                <Video className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <span className="font-display font-semibold text-lg">Videoepreneur</span>
            </Link>
            <p className="text-sm text-[#4A4D57] mt-4 max-w-xs leading-relaxed">
              India's #1 webinar automation OS. Built for teachers, creators, and coaches.
            </p>
          </div>
          <div>
            <div className="vp-label mb-3">Product</div>
            <ul className="space-y-2 text-sm text-[#4A4D57]">
              <li><a href="#features" className="hover:text-[#0B0D17]">Features</a></li>
              <li><a href="#pricing" className="hover:text-[#0B0D17]">Pricing</a></li>
              <li><Link to="/signup" className="hover:text-[#0B0D17]">Sign up</Link></li>
              <li><Link to="/login" className="hover:text-[#0B0D17]">Log in</Link></li>
            </ul>
          </div>
          <div>
            <div className="vp-label mb-3">Company</div>
            <ul className="space-y-2 text-sm text-[#4A4D57]">
              <li><Link to="/blog" className="hover:text-[#0B0D17]">Blog</Link></li>
              <li><a href="#stories" className="hover:text-[#0B0D17]">Customer stories</a></li>
              <li><a href="#faq" className="hover:text-[#0B0D17]">FAQ</a></li>
              <li><a href="mailto:hello@videoepreneur.com" className="hover:text-[#0B0D17]">Contact</a></li>
            </ul>
          </div>
          <div>
            <div className="vp-label mb-3">Trust</div>
            <ul className="space-y-2 text-sm text-[#4A4D57]">
              <li>Meta BSP verified</li>
              <li>Razorpay-secured payments</li>
              <li>99.9% uptime SLA</li>
              <li>Made with ♥ in India</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#E4E6EC]">
          <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#747680]">
            <div>© {new Date().getFullYear()} Videoepreneur. All rights reserved.</div>
            <div className="flex gap-4">
              <a href="/privacy" className="hover:text-[#0B0D17]">Privacy</a>
              <a href="/terms" className="hover:text-[#0B0D17]">Terms</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ============ Sticky mobile compare ribbon ============ */}
      <div
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0B0D17] text-white px-4 py-3 flex items-center gap-3 border-t border-white/10 shadow-[0_-8px_24px_rgba(0,0,0,0.15)]"
        data-testid="marketing-mobile-ribbon"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#F0ABFC] font-bold">Zoom se switch karo</div>
          <div className="text-sm font-display font-bold leading-tight mt-0.5">Save ₹22K+/mo · 93% cheaper</div>
        </div>
        <Link
          to="/signup"
          data-testid="marketing-mobile-ribbon-cta"
          className="shrink-0 px-4 py-2 rounded-full bg-gradient-to-r from-[#F0ABFC] to-[#22D3EE] text-[#0B0D17] font-bold text-xs inline-flex items-center gap-1.5"
        >
          Free me shuru <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* ============ Demo video modal ============ */}
      {demoOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 vp-fade-up"
          onClick={() => setDemoOpen(false)}
          data-testid="marketing-demo-modal"
        >
          <div
            className="w-full max-w-4xl rounded-2xl overflow-hidden bg-black relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDemoOpen(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white flex items-center justify-center"
              data-testid="marketing-demo-close"
              aria-label="Close demo"
            >
              ✕
            </button>
            <div className="aspect-video bg-gradient-to-br from-[#0B0D17] via-[#1E1B4B] to-[#4C1D95] relative overflow-hidden">
              {/* Ambient glow */}
              <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(circle at 30% 30%, rgba(11,92,255,0.6), transparent 55%), radial-gradient(circle at 70% 70%, rgba(240,171,252,0.4), transparent 50%)" }} />
              {/* Placeholder content */}
              <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
                <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center mb-4">
                  <PlayCircle className="w-8 h-8 text-white" strokeWidth={1.5} />
                </div>
                <div className="text-[11px] uppercase tracking-widest text-[#F0ABFC] font-bold mb-2">2-Minute Product Tour</div>
                <div className="font-display font-black text-2xl sm:text-3xl text-white leading-tight max-w-xl">
                  Idea → landing page → payment → live webinar in 90 minutes
                </div>
                <div className="mt-6 grid grid-cols-5 gap-2 max-w-md w-full">
                  {[
                    { l: "01 Landing", c: "#0B5CFF" },
                    { l: "02 Payment", c: "#059669" },
                    { l: "03 Zoom", c: "#7C3AED" },
                    { l: "04 WhatsApp", c: "#25D366" },
                    { l: "05 Certificate", c: "#F5A623" },
                  ].map((s, i) => (
                    <div
                      key={s.l}
                      className="rounded-md px-1.5 py-2 text-[9px] font-bold uppercase tracking-wider border border-white/15 backdrop-blur-sm"
                      style={{ color: s.c, background: s.c + "18" }}
                    >
                      {s.l}
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-white/50 mt-6 font-mono">Video demo coming soon · sign up karo aur inbox me link mile</div>
              </div>
            </div>
            <div className="bg-[#0B0D17] px-6 py-4 text-white flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-bold text-lg">Idea → paid webinar in 90 minutes</div>
                <div className="text-xs text-white/60 mt-0.5">2 min me pura workflow — landing page, payment, WhatsApp, certificate.</div>
              </div>
              <Link
                to="/signup"
                onClick={() => setDemoOpen(false)}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-[#F0ABFC] to-[#22D3EE] text-[#0B0D17] font-bold text-sm inline-flex items-center gap-1.5"
                data-testid="marketing-demo-cta"
              >
                Free me shuru karo <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
